<template>
	<AppPage ref="page">
		<AppCardPage v-if="currentMeeting" class="grow !px-0 !py-0">
			<div
				class="grid w-full flex-1 grid-cols-[minmax(0,1fr)_18rem] items-start gap-4 max-[1023px]:grid-cols-1"
			>
				<div
					class="flex min-h-full min-w-0 flex-col border border-t-0 border-[var(--border)] bg-white"
				>
					<nav
						class="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b bg-white px-4"
					>
						<AppLink
							:to="{ name: 'MeetingsOverview' }"
							icon="arrow_back"
							class="self-center py-4"
						>
							Terug naar overzicht
						</AppLink>
						<AppTabBar
							class="self-end !p-0"
							:tabs="[
								{ name: 'Samenvatting', id: 1 },
								{ name: 'Transcript', id: 2 },
							]"
						/>
					</nav>
					<div class="grow bg-white p-4">
						<div class="flex flex-col pb-2">
							<h1
								class="text-2xl py-1"
								title="Klik om titel te bewerken"
								contenteditable="plaintext-only"
								@focus="editingTitle = getTitle(currentMeeting)"
								@input="editingTitle = ($event.target as HTMLElement).innerText"
								@blur="saveTitle"
								@keydown.enter.prevent="blur($event)"
								@keydown.escape="blur($event, false)"
							>
								{{ getTitle(currentMeeting) }}
							</h1>
						</div>
						<router-view></router-view>
					</div>
				</div>

				<aside
					class="sticky top-6 flex flex-col gap-3 rounded-[var(--border-radius)] border border-[var(--border)] bg-linear-to-b from-white to-[#f8fafc] p-4 max-[1023px]:order-[-1] max-[1023px]:static"
				>
					<div
						class="flex flex-col gap-1.5 border-b border-[var(--border)] pb-3 last:border-b-0 last:pb-0"
					>
						<p
							class="m-0 text-[0.75rem] leading-4 tracking-[0.04em] text-[var(--text-muted)] uppercase"
						>
							Datum
						</p>
						<p
							class="m-0 text-[0.95rem] leading-[1.4rem] font-semibold text-[var(--text-primary)]"
						>
							{{ meetingDate }}
						</p>
					</div>

					<div
						class="flex flex-col gap-1.5 border-b border-[var(--border)] pb-3 last:border-b-0 last:pb-0"
					>
						<p
							class="m-0 text-[0.75rem] leading-4 tracking-[0.04em] text-[var(--text-muted)] uppercase"
						>
							Gespreksduur
						</p>
						<p
							class="m-0 text-[0.95rem] leading-[1.4rem] font-semibold text-[var(--text-primary)]"
						>
							{{ meetingDuration }}
						</p>
					</div>

					<div
						class="flex flex-col gap-1.5 border-b border-[var(--border)] pb-3 last:border-b-0 last:pb-0"
					>
						<p
							class="m-0 text-[0.75rem] leading-4 tracking-[0.04em] text-[var(--text-muted)] uppercase"
						>
							Dossier
						</p>
						<AppButton
							v-if="linkedDossier && linkedDossier.displayId"
							as="RouterLink"
							variant="link"
							:to="{
								name: 'SingleDossier',
								params: { dossierDisplayId: linkedDossier.displayId },
							}"
							class="max-w-full self-start !p-0 text-[0.95rem] leading-[1.4rem] font-normal no-underline [&>span:last-child]:block [&>span:last-child]:max-w-full [&>span:last-child]:truncate"
						>
							{{ linkedDossier.name }}
						</AppButton>
						<p
							v-else-if="linkedDossier"
							class="m-0 text-[0.95rem] leading-[1.4rem] font-semibold text-[var(--text-primary)]"
						>
							{{ linkedDossier.name }}
						</p>
						<p
							v-else-if="currentMeeting?.dossierId"
							class="m-0 text-[0.85rem] leading-5 text-[var(--text-muted)]"
						>
							Dossier laden...
						</p>
						<AppButton
							v-if="currentMeeting?.dossierId"
							variant="danger"
							icon="link_off"
							class="w-fit self-start"
							@click="unlinkConversationFromDossier"
						>
							Ontkoppelen
						</AppButton>
						<AppPopoverSelect
							v-else-if="isSelectingDossier"
							v-model="selectedDossierId"
							:options="dossierOptions"
							placeholder="Zoek een dossier"
							empty-state-label="Geen dossiers gevonden"
							:auto-open="true"
							@select="linkConversationToDossier"
							@close="closeDossierPicker"
						/>
						<AppButton
							v-else
							variant="primary"
							icon="link"
							class="w-fit self-start"
							@click="openDossierPicker"
						>
							Koppelen aan dossier
						</AppButton>
					</div>
				</aside>
			</div>
		</AppCardPage>
	</AppPage>
</template>

<script setup lang="ts">
import { DateTime } from "luxon";
import { useRoute } from "vue-router";
import { useQuery, useZero } from "@/composables/useZero";
import { useUserStore } from "@/store/useUserStore";
import { computed, provide, watch, ref } from "vue";
import { conversationKey } from "@/keys";
import router from "@/router";
import { getTitle } from "@/helpers/conversationHelpers";
import { formatTimestamp } from "@/helpers/uiHelpers";
import { toast } from "vue-sonner";
import { mutators, queries } from "@loanlink-nl/zero";

const route = useRoute();
const userStore = useUserStore();

const z = useZero();

const editingTitle = ref("");

const { data: currentMeeting } = useQuery(() => {
	const agencyId = userStore.user?.agencyId ?? "";
	const meetingId = route.params.meetingId as string;
	const isDisplayId = Number.isFinite(Number(meetingId));

	return queries.conversations.byId({
		agencyId,
		idField: isDisplayId ? "displayId" : "id",
		id: isDisplayId ? Number(meetingId) : meetingId,
	});
});
const { data: linkedDossier } = useQuery(() =>
	queries.dossier.byId({
		field: "id",
		id: currentMeeting.value?.dossierId ?? "",
	}),
);
const { data: availableDossiers } = useQuery(() => queries.dossier.all());

const meetingDate = computed(() =>
	formatDate(currentMeeting.value?.createdAt ?? null),
);
const meetingDuration = computed(() => {
	const durationInSeconds = currentMeeting.value?.durationInSeconds;
	if (durationInSeconds == null) {
		return "-";
	}

	return formatTimestamp(durationInSeconds);
});
const dossierOptions = computed(() =>
	(availableDossiers.value ?? []).map((dossier) => ({
		value: dossier.id,
		label: dossier.name,
	})),
);

const isSelectingDossier = ref(false);
const selectedDossierId = ref<string>();

provide(conversationKey, currentMeeting);

watch(
	() => currentMeeting.value?.dossierId,
	(dossierId) => {
		selectedDossierId.value = dossierId ?? undefined;
		if (dossierId) {
			isSelectingDossier.value = false;
		}
	},
	{ immediate: true },
);

watch(
	() => currentMeeting.value?.displayId,
	async (id) => {
		if (id) {
			await router.replace({
				params: {
					...route.params,
					meetingId: id,
				},
			});
		}
	},
	{ immediate: true },
);

function formatDate(date?: number | null) {
	if (date == null) {
		return "-";
	}

	return new Intl.DateTimeFormat("nl-NL", {
		day: "numeric",
		month: "long",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(DateTime.fromMillis(date).toJSDate());
}

const shouldPersist = ref(true);

function blur(event: Event, persist = true) {
	shouldPersist.value = persist;
	(event.target as HTMLElement)?.blur();
}

function saveTitle(event: Event) {
	if (!currentMeeting.value) {
		shouldPersist.value = true;
		return;
	}

	const currentTitle = getTitle(currentMeeting.value);
	if (currentTitle === editingTitle.value) {
		shouldPersist.value = true;
		return;
	}

	if (shouldPersist.value) {
		const newTitle = editingTitle.value.trim();
		z.value.mutate(
			mutators.conversationTable.setTitle({
				id: currentMeeting.value.id,
				title: newTitle === "" ? null : newTitle,
			}),
		);
	}

	(event.target as HTMLElement).innerText = getTitle(currentMeeting.value);
	shouldPersist.value = true;
}

function openDossierPicker() {
	selectedDossierId.value = currentMeeting.value?.dossierId ?? undefined;
	isSelectingDossier.value = true;
}

function closeDossierPicker() {
	isSelectingDossier.value = false;
	selectedDossierId.value = currentMeeting.value?.dossierId ?? undefined;
}

async function linkConversationToDossier(dossierId: string) {
	if (!currentMeeting.value) {
		return;
	}

	try {
		await z.value.mutate(
			mutators.conversationTable.setDossier({
				id: currentMeeting.value.id,
				dossierId,
			}),
		).server;
	} catch {
		selectedDossierId.value = currentMeeting.value.dossierId ?? undefined;
		toast.error("Gesprek koppelen aan dossier is niet gelukt");
	}
}

async function unlinkConversationFromDossier() {
	if (!currentMeeting.value) {
		return;
	}

	try {
		const mutation = z.value.mutate(
			mutators.conversationTable.setDossier({
				id: currentMeeting.value.id,
				dossierId: null,
			}),
		);
		selectedDossierId.value = undefined;
		isSelectingDossier.value = true;
	} catch {
		closeDossierPicker();
		toast.error("Gesprek ontkoppelen van dossier is niet gelukt");
	}
}
</script>
